// The "sex assigned at birth" and "gender identity" values defined in this file, and the UI components
// that use them, follow requirements and recommendations outlined by Scientific Affairs.
// Consult with Scientific Affairs before modifying any content, options, or patterns for
// sex & gender collection.

import { invert, snakeCase } from 'lodash-es';

export interface IGenderIdentityFieldNames {
  genderIdentityFieldName: string;
  anotherGenderFieldName: string;
}

export interface ISexAndGenderIdentityFieldNames extends IGenderIdentityFieldNames {
  sexAssignedAtBirthFieldName: string;
}

export interface ISexualOrientationIdentityFieldNames {
  sexualOrientationFieldName: string;
  notListedFieldName: string;
}

/**
 * Sex Assigned at Birth values that are sent to and received from the server.
 */
export enum SexAssignedAtBirth {
  FEMALE = 'F',
  MALE = 'M',
}

/**
 * Gender Identity values that are sent to and received from the server.
 * They currently match the display values, but are not guaranteed to match in the future.
 */
export enum GenderIdentity {
  WOMAN = 'Female/woman',
  MAN = 'Male/man',
  TRANS_WOMAN = 'Transgender female/woman',
  TRANS_MAN = 'Transgender male/man',
  NONBINARY_GENDERQUEER = 'Nonbinary, genderqueer, or not exclusively female or male',
  ANOTHER_GENDER = 'Another gender',
  DO_NOT_KNOW = "Don't know",
  PREFER_NOT_TO_SAY = 'Prefer not to say',
}

/**
 * This will soon be deprecated in favor of the new GenderIdentity enum.
 */
export enum GENDER_IDENTITIES {
  FEMALE = 'F',
  MALE = 'M',
  NON_BINARY = 'non-binary',
  SELF_DESCRIBED = 'self-described',
  PREFER_NOT_TO_RESPOND = 'prefer not to respond',
  TRANS_MALE = 'trans-male',
  TRANS_FEMALE = 'trans-female',
  NOT_LISTED = 'not-listed',
}

export const djangoValuesByGenderIdentity: Record<GenderIdentity, string> = {
  [GenderIdentity.WOMAN]: 'F',
  [GenderIdentity.MAN]: 'M',
  [GenderIdentity.TRANS_WOMAN]: 'trans-female',
  [GenderIdentity.TRANS_MAN]: 'trans-male',
  [GenderIdentity.NONBINARY_GENDERQUEER]: 'non-binary',
  [GenderIdentity.ANOTHER_GENDER]: 'self-described',
  [GenderIdentity.DO_NOT_KNOW]: 'dont-know',
  [GenderIdentity.PREFER_NOT_TO_SAY]: 'prefer not to respond',
};

export const genderIdentityByDjangoValues: Record<string, GenderIdentity> = invert(
  djangoValuesByGenderIdentity
) as Record<string, GenderIdentity>;

export enum GenderIdentityDataSharingRequirement {
  SENT_TO_EXTERNAL_EMR,
  SENT_TO_STATE_FEDERAL_DASHBOARD,
  SHARED_WITH_CLINICIAN_WHEN_APPROPRIATE,
}

export const SEX_ASSIGNED_AT_BIRTH_FIELD_NAME = 'sex';
export const GENDER_IDENTITY_FIELD_NAME = 'genderIdentity';
export const ANOTHER_GENDER_FIELD_NAME = 'selfDescribedGenderIdentity';
export const SEXUAL_ORIENTATION_FIELD_NAME = 'sexualOrientation';
export const NOT_LISTED_FIELD_NAME = 'selfDescribedSexualOrientation';

export const GENDER_IDENTITY_CAMEL_CASE_FIELD_NAMES: Record<
  keyof IGenderIdentityFieldNames,
  string
> = {
  genderIdentityFieldName: GENDER_IDENTITY_FIELD_NAME,
  anotherGenderFieldName: ANOTHER_GENDER_FIELD_NAME,
};

/**
 * Sexual Orientation Identity values that are sent to and received from the server.
 * They currently match the display values, but are not guaranteed to match in the future.
 */
export enum SexualOrientationIdentity {
  LESBIAN_OR_GAY = 'Lesbian or gay',
  STRAIGHT_OR_HETEROSEXUAL = 'Straight or heterosexual',
  BISEXUAL = 'Bisexual',
  QUEER = 'Queer',
  ASEXUAL = 'Asexual',
  PANSEXUAL = 'Pansexual',
  DO_NOT_KNOW = "Don't know",
  PREFER_NOT_TO_SAY = 'Prefer not to say',
  NOT_LISTED = 'Not Listed',
}

/**
 * Default field names for Sex Assigned at Birth, Gender Identity, and Another Gender
 * fields. Used in Sex and Gender utilities and UI components.
 */
export const SEX_AND_GENDER_IDENTITY_CAMEL_CASE_FIELD_NAMES: ISexAndGenderIdentityFieldNames = {
  sexAssignedAtBirthFieldName: SEX_ASSIGNED_AT_BIRTH_FIELD_NAME,
  ...GENDER_IDENTITY_CAMEL_CASE_FIELD_NAMES,
};

export const GENDER_IDENTITY_SNAKE_CASE_FIELD_NAMES: IGenderIdentityFieldNames = {
  genderIdentityFieldName: snakeCase(GENDER_IDENTITY_FIELD_NAME),
  anotherGenderFieldName: snakeCase(ANOTHER_GENDER_FIELD_NAME),
};

export const SEXUAL_ORIENTATION_CAMEL_CASE_FIELD_NAMES: ISexualOrientationIdentityFieldNames = {
  sexualOrientationFieldName: SEXUAL_ORIENTATION_FIELD_NAME,
  notListedFieldName: NOT_LISTED_FIELD_NAME,
};
